"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { getBrowserPlatformApiClient } from "~/lib/trpc/browser-client";
import { useAppAuth } from "~/lib/auth/app-auth";
import { useExecutionOperationLive } from "~/features/execution/operation-live";
import {
  applyExecutionDetail,
  isTerminalExecutionState,
} from "./execution-state";
import {
  registerToastExecutionBindings,
  resetToastExecutionBindings,
} from "./execution-bindings";
import { preferLatestHandle } from "./tracking/merge";
import { isParkedExecution } from "./tracking/park-policy";
import {
  isFeedOperationDiscoverable,
  mapFeedOperationToHandle,
  mapFeedOperationToSummary,
  type ExecutionFeedOperation,
} from "./tracking/feed-operation";
import {
  isExecutionToastSuppressed,
  subscribeExecutionToastSuppression,
} from "./suppression";
import {
  dismissSimpleToast,
  getSimpleToasts,
  subscribeSimpleToasts,
} from "./simple-toast-store";
import type { TrackedExecutionRecord } from "./tracking/record";
import { ExecutionToastModal } from "./ui/execution-toast-modal";
import { CenterToastStack } from "./ui/execution-toast-stack";
import {
  buildExecutionToastView,
  type CenterToastItem,
} from "./ui/toast-view-model";
import type {
  ExecutionDisplayContext,
  ExecutionHandle,
  ToastId,
  TrackExecutionOptions,
  TrackExecutionSource,
  TrackExecutionTerminalPayload,
  TrackExecutionUpdatePayload,
} from "./types";

// Stable empty list handed to the stack while the detail modal is open, so
// pills exit-animate instead of hard-unmounting (and memoized children keep
// their reference equality).
const EMPTY_TOAST_ITEMS: readonly CenterToastItem[] = [];

const ACTIVE_TRACKED_EXECUTION_TTL_MS = 6 * 60 * 60 * 1000;
/** Watched (dismissed-but-tracked) ops must survive as long as the user feed's
 * terminal hydration window so a reconciler completion can still re-surface. */
const WATCHING_TRACKED_EXECUTION_TTL_MS = 24 * 60 * 60 * 1000;
const DEFAULT_TERMINAL_DURATION_MS = 8_000;
const POLL_INTERVAL_MS = 2_000;
/** Slow cadence for watching/parked records — the feed socket is the primary
 * signal there; polling is only a safety net for engine-run ops. */
const WATCH_POLL_INTERVAL_MS = 60_000;
/** No live frame (stream/feed) for this long on a non-terminal record → arm
 * detail polling in parallel. The tRPC stream is silently dead for worker ops
 * (only the legacy engine publishes Redis events), so silence is a first-class
 * failure mode, not an edge case. */
const IDLE_WATCHDOG_MS = 10_000;
const WATCHDOG_SWEEP_INTERVAL_MS = 2_500;
/** Settlement-parked ops auto-dismiss into watching after this long. */
const PARK_AUTO_DISMISS_MS = 12_000;
/** Grace before an untracked feed op auto-surfaces, so surface-owned recovery
 * (trade-ticket auto-reopen) can claim suppression first. */
const FEED_DISCOVERY_GRACE_MS = 1_500;
const SESSION_STORAGE_PREFIX = "glider:webapp-v2:tracked-executions";

function getRecordPhase(record: TrackedExecutionRecord): string | null {
  return (
    record.summary?.phase ??
    record.handle.progress?.phase ??
    record.liveOperation?.currentPhase ??
    null
  );
}

function isRecordParked(record: TrackedExecutionRecord): boolean {
  return isParkedExecution(record.handle.state, getRecordPhase(record));
}

interface ExecutionCallbacks {
  onTerminal?: TrackExecutionOptions["onTerminal"];
  onUpdate?: TrackExecutionOptions["onUpdate"];
}

interface PersistedTrackedExecution {
  displayContext?: ExecutionDisplayContext | null;
  handle: ExecutionHandle;
  operationId: string;
  presentation?: "visible" | "watching";
  savedAt: number;
  source: TrackExecutionSource;
  terminalDurationMs: number;
  terminalReported?: boolean;
}

function getStablePrivyUserId(user: unknown): string | null {
  if (!user || typeof user !== "object") {
    return null;
  }

  const id = (user as Record<string, unknown>).id;
  return typeof id === "string" && id.trim().length > 0 ? id : null;
}

function getExecutionStorageKey(userId: string | null): string | null {
  if (!userId) {
    return null;
  }

  return `${SESSION_STORAGE_PREFIX}:${userId}`;
}

function readPersistedTrackedExecutions(
  storageKey: string | null,
): PersistedTrackedExecution[] {
  if (!storageKey || typeof window === "undefined") {
    return [];
  }

  const rawValue = window.sessionStorage.getItem(storageKey);
  if (!rawValue) {
    return [];
  }

  try {
    const parsed = JSON.parse(rawValue) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    const now = Date.now();

    return parsed.filter((entry): entry is PersistedTrackedExecution => {
      if (!entry || typeof entry !== "object") {
        return false;
      }

      const candidate = entry as Partial<PersistedTrackedExecution>;
      const ttlMs =
        candidate.presentation === "watching"
          ? WATCHING_TRACKED_EXECUTION_TTL_MS
          : ACTIVE_TRACKED_EXECUTION_TTL_MS;
      return (
        typeof candidate.operationId === "string" &&
        typeof candidate.savedAt === "number" &&
        now - candidate.savedAt <= ttlMs &&
        candidate.handle !== undefined &&
        (candidate.source === "agent" || candidate.source === "user") &&
        typeof candidate.terminalDurationMs === "number" &&
        (candidate.presentation === undefined ||
          candidate.presentation === "visible" ||
          candidate.presentation === "watching")
      );
    });
  } catch {
    return [];
  }
}

function writePersistedTrackedExecutions(
  storageKey: string | null,
  records: Record<string, TrackedExecutionRecord>,
): void {
  if (!storageKey || typeof window === "undefined") {
    return;
  }

  const persisted: PersistedTrackedExecution[] = Object.values(records).flatMap(
    (record) =>
      isTerminalExecutionState(record.handle.state)
        ? []
        : [
            {
              displayContext: record.displayContext,
              handle: record.handle,
              operationId: record.operationId,
              presentation: record.presentation,
              savedAt: Date.now(),
              source: record.source,
              terminalDurationMs: record.terminalDurationMs,
              terminalReported: record.terminalReported,
            },
          ],
  );

  if (persisted.length === 0) {
    window.sessionStorage.removeItem(storageKey);
    return;
  }

  window.sessionStorage.setItem(storageKey, JSON.stringify(persisted));
}

function useTrackedExecutionToasts() {
  const { authenticated, user } = useAppAuth();
  const platformApi = useMemo(() => getBrowserPlatformApiClient(), []);
  const [activeOperationId, setActiveOperationId] = useState<string | null>(
    null,
  );
  const [isStackHovered, setIsStackHovered] = useState(false);
  const [executions, setExecutions] = useState<
    Record<string, TrackedExecutionRecord>
  >({});
  const executionsRef = useRef<Record<string, TrackedExecutionRecord>>({});
  const callbacksRef = useRef(new Map<string, ExecutionCallbacks>());
  const pollingActiveRef = useRef(new Set<string>());
  const pollingTimeoutsRef = useRef(
    new Map<string, ReturnType<typeof setTimeout>>(),
  );
  const terminalNotifiedRef = useRef(new Set<string>());
  const previousStorageKeyRef = useRef<string | null>(null);
  /** Wall-clock ms of the last LIVE frame per op (feed frame or registration).
   * Lives outside React state on purpose: activity stamps must not churn
   * renders, persistence, or effect timers. Poll results never stamp it. */
  const lastActivityAtRef = useRef(new Map<string, number>());
  /** Storage keys already hydrated this mount — hydration must not re-run
   * (and re-stamp activity) when unrelated callback identities change. */
  const hydratedStorageKeysRef = useRef(new Set<string>());
  /** Mirrors activeOperationId for long-lived closures (poll chains). */
  const activeOperationIdRef = useRef<string | null>(null);
  /** Set on unmount so in-flight polls cannot resurrect their chains. */
  const disposedRef = useRef(false);
  /** Auto-close timers keyed by toast id — armed once per eligible toast,
   * NOT re-armed by unrelated state churn. */
  const autoCloseTimersRef = useRef(
    new Map<string, ReturnType<typeof setTimeout>>(),
  );
  /** Run-DO socket status for the open modal — read by its detail poll. */
  const operationLiveStatusRef = useRef<string>("idle");
  const pendingDiscoveryTimersRef = useRef(
    new Map<string, ReturnType<typeof setTimeout>>(),
  );
  /** Freshest feed frame per operation, replaced wholesale on every publish —
   * read at discovery-grace expiry so a mid-window transition never surfaces
   * from a stale closure capture. */
  const latestFeedFramesRef = useRef(
    new Map<string, ExecutionFeedOperation>(),
  );
  const [suppressionVersion, setSuppressionVersion] = useState(0);
  const userId = getStablePrivyUserId(user);
  const storageKey = getExecutionStorageKey(authenticated ? userId : null);

  useEffect(
    () =>
      subscribeExecutionToastSuppression(() => {
        setSuppressionVersion((version) => version + 1);
      }),
    [],
  );

  useEffect(() => {
    activeOperationIdRef.current = activeOperationId;
  }, [activeOperationId]);

  const commitExecutions = useCallback(
    (nextExecutions: Record<string, TrackedExecutionRecord>) => {
      executionsRef.current = nextExecutions;
      setExecutions(nextExecutions);
    },
    [],
  );

  const cleanupTransport = useCallback((operationId: string) => {
    const pollTimeout = pollingTimeoutsRef.current.get(operationId);
    if (pollTimeout) {
      clearTimeout(pollTimeout);
      pollingTimeoutsRef.current.delete(operationId);
    }

    pollingActiveRef.current.delete(operationId);
  }, []);

  const buildUpdatePayload = useCallback(
    (
      record: TrackedExecutionRecord,
      event: TrackExecutionUpdatePayload["event"],
    ) => ({
      detail: record.detail,
      event,
      handle: record.handle,
      operationId: record.operationId,
      summary: record.summary,
    }),
    [],
  );

  const emitExecutionCallbacks = useCallback(
    (
      record: TrackedExecutionRecord,
      event: TrackExecutionUpdatePayload["event"],
    ) => {
      const callbacks = callbacksRef.current.get(record.operationId);
      if (!callbacks) {
        return;
      }

      const payload = buildUpdatePayload(record, event);
      callbacks.onUpdate?.(payload);

      if (
        isTerminalExecutionState(record.handle.state) &&
        !terminalNotifiedRef.current.has(record.operationId)
      ) {
        terminalNotifiedRef.current.add(record.operationId);
        callbacks.onTerminal?.({
          ...payload,
          state: record.handle.state,
        } satisfies TrackExecutionTerminalPayload);
      }
    },
    [buildUpdatePayload],
  );

  const upsertExecution = useCallback(
    (
      operationId: string,
      updater: (
        currentRecord: TrackedExecutionRecord | null,
      ) => TrackedExecutionRecord,
    ): TrackedExecutionRecord => {
      const currentRecord = executionsRef.current[operationId] ?? null;
      const nextRecord = updater(currentRecord);
      commitExecutions({
        ...executionsRef.current,
        [operationId]: nextRecord,
      });
      return nextRecord;
    },
    [commitExecutions],
  );

  const removeExecution = useCallback(
    (operationId: string) => {
      cleanupTransport(operationId);
      callbacksRef.current.delete(operationId);
      terminalNotifiedRef.current.delete(operationId);
      lastActivityAtRef.current.delete(operationId);

      // Ref, not state: long-lived poll-chain closures capture this callback
      // and must still close the modal for the record they delete.
      if (activeOperationIdRef.current === operationId) {
        setActiveOperationId(null);
      }

      if (!executionsRef.current[operationId]) {
        return;
      }

      const nextExecutions = { ...executionsRef.current };
      delete nextExecutions[operationId];
      commitExecutions(nextExecutions);
    },
    [cleanupTransport, commitExecutions],
  );

  const clearTrackedExecutions = useCallback(() => {
    callbacksRef.current.clear();
    terminalNotifiedRef.current.clear();

    for (const timeoutId of pendingDiscoveryTimersRef.current.values()) {
      clearTimeout(timeoutId);
    }
    pendingDiscoveryTimersRef.current.clear();
    latestFeedFramesRef.current.clear();

    for (const operationId of Object.keys(executionsRef.current)) {
      cleanupTransport(operationId);
    }

    lastActivityAtRef.current.clear();
    commitExecutions({});
    setActiveOperationId(null);
  }, [cleanupTransport, commitExecutions]);

  /**
   * Dismissing a non-terminal toast does NOT stop tracking: the record moves
   * to "watching" (hidden, feed frames still merge, slow poll as safety net)
   * and re-surfaces exactly once when it reaches a terminal state — including
   * completions written by the reconciliation cron long after dismissal.
   */
  const transitionExecutionToWatching = useCallback(
    (operationId: string) => {
      const record = executionsRef.current[operationId];
      if (!record) {
        return;
      }

      if (isTerminalExecutionState(record.handle.state)) {
        removeExecution(operationId);
        return;
      }

      if (record.presentation === "watching") {
        return;
      }

      upsertExecution(operationId, (current) => ({
        ...(current ?? record),
        presentation: "watching",
      }));
    },
    [removeExecution, upsertExecution],
  );

  /**
   * Runs after any update that produced a terminal record: a terminal that
   * arrived while an inline surface was suppressing the toast is never
   * re-toasted (the surface showed the outcome); a terminal on a watching
   * record re-surfaces as a fresh visible toast exactly once; a visible
   * terminal just marks itself reported.
   */
  const reconcileTerminalPresentation = useCallback(
    (operationId: string) => {
      const record = executionsRef.current[operationId];
      if (!record || !isTerminalExecutionState(record.handle.state)) {
        return;
      }

      if (isExecutionToastSuppressed(operationId) && !record.terminalReported) {
        removeExecution(operationId);
        return;
      }

      if (record.terminalReported && record.presentation === "visible") {
        return;
      }

      upsertExecution(operationId, (current) => ({
        ...(current ?? record),
        presentation: "visible",
        terminalReported: true,
      }));
    },
    [removeExecution, upsertExecution],
  );

  /** One-shot detail fetch to recover the failure reason when a feed frame
   * (which never carries one) delivered the terminal state. */
  const hydrateTerminalReason = useCallback(
    async (operationId: string) => {
      try {
        const detail = await platformApi.v2.executions.detail.query({
          operationId,
        });

        if (disposedRef.current || !executionsRef.current[operationId]) {
          return;
        }

        const seedRecord = executionsRef.current[operationId];
        if (!seedRecord) {
          return;
        }

        const hydratedState = applyExecutionDetail(detail);
        upsertExecution(operationId, (current) => {
          const base = current ?? seedRecord;
          return {
            ...base,
            detail: hydratedState.detail,
            handle: preferLatestHandle(base.handle, hydratedState.handle),
            summary: hydratedState.summary,
          };
        });
      } catch {
        // Best-effort — the modal's open-triggered detail fetch is the backup.
      }
    },
    [platformApi, upsertExecution],
  );

  /**
   * Whether the poll chain should keep running. With no active stream this is
   * the classic fallback: poll until terminal. With a live stream open,
   * polling is the idle watchdog's parallel safety net — it stops as soon as
   * live frames (stream or feed) resume.
   */
  const shouldContinuePolling = useCallback((operationId: string): boolean => {
    const record = executionsRef.current[operationId];
    if (!record || isTerminalExecutionState(record.handle.state)) {
      return false;
    }

    // Until the user feed has claimed the operation with a frame, polling IS
    // the transport (legacy-engine ops may never appear on the feed). Once
    // frames flow, polling stops and the sweep watchdog re-arms it only if
    // the feed goes silent.
    if (record.liveOperation === null) {
      return true;
    }

    const lastActivityAtMs = lastActivityAtRef.current.get(operationId) ?? 0;
    return Date.now() - lastActivityAtMs >= IDLE_WATCHDOG_MS;
  }, []);

  const startPolling = useCallback(
    (operationId: string) => {
      const currentRecord = executionsRef.current[operationId];
      if (
        !currentRecord ||
        isTerminalExecutionState(currentRecord.handle.state)
      ) {
        return;
      }

      if (
        pollingTimeoutsRef.current.has(operationId) ||
        pollingActiveRef.current.has(operationId)
      ) {
        return;
      }

      pollingActiveRef.current.add(operationId);

      const stopPolling = () => {
        pollingActiveRef.current.delete(operationId);
      };

      const scheduleNextPoll = () => {
        const record = executionsRef.current[operationId];
        if (!record || !shouldContinuePolling(operationId)) {
          stopPolling();
          return;
        }

        const intervalMs =
          record.presentation === "watching" || isRecordParked(record)
            ? WATCH_POLL_INTERVAL_MS
            : POLL_INTERVAL_MS;
        const timeoutId = setTimeout(() => {
          pollingTimeoutsRef.current.delete(operationId);
          void poll();
        }, intervalMs);

        pollingTimeoutsRef.current.set(operationId, timeoutId);
      };

      const poll = async () => {
        const latestRecord = executionsRef.current[operationId];
        if (!latestRecord) {
          stopPolling();
          return;
        }

        try {
          const detail = await platformApi.v2.executions.detail.query({
            operationId,
          });

          // The provider may have unmounted while the query was in flight —
          // without this guard the resolved poll would re-arm its own chain
          // forever (executionsRef is not cleared on unmount).
          if (disposedRef.current || !executionsRef.current[operationId]) {
            stopPolling();
            return;
          }

          // No-op guard: identical detail must not commit a new state object.
          // Every commit re-renders the stack and (before this guard) reset
          // every auto-close timer and the watchdog sweep — 2s poll churn was
          // enough to starve both indefinitely.
          const current = executionsRef.current[operationId];
          const unchanged =
            current !== undefined &&
            current.detail !== null &&
            current.handle.updatedAt === detail.operation.updatedAt &&
            current.handle.state === detail.operation.state &&
            current.detail.timeline.length === detail.timeline.length &&
            current.summary?.headline === detail.summary.headline &&
            current.summary?.detail === detail.summary.detail &&
            current.summary?.phase === detail.summary.phase &&
            current.summary?.stepIndex === detail.summary.stepIndex &&
            current.summary?.terminalReason === detail.summary.terminalReason;

          if (!unchanged) {
            const hydratedState = applyExecutionDetail(detail);
            const nextRecord = upsertExecution(operationId, (record) => ({
              ...(record ?? latestRecord),
              detail: hydratedState.detail,
              handle: preferLatestHandle(
                record?.handle ?? null,
                hydratedState.handle,
              ),
              summary: hydratedState.summary,
            }));

            emitExecutionCallbacks(nextRecord, null);

            if (isTerminalExecutionState(nextRecord.handle.state)) {
              cleanupTransport(operationId);
              reconcileTerminalPresentation(operationId);
              return;
            }
          }
        } catch {
          // Keep the toast alive and try again on the next interval.
        }

        if (disposedRef.current || !executionsRef.current[operationId]) {
          stopPolling();
          return;
        }

        scheduleNextPoll();
      };

      void poll();
    },
    [
      cleanupTransport,
      emitExecutionCallbacks,
      platformApi,
      reconcileTerminalPresentation,
      shouldContinuePolling,
      upsertExecution,
    ],
  );

  const applyFeedOperation = useCallback(
    (operation: ExecutionFeedOperation) => {
      const operationId = operation.operationId;
      const currentRecord = executionsRef.current[operationId];
      if (!currentRecord) {
        return;
      }

      // Skip frames we have already applied — feed pushes re-deliver the whole
      // operations array on every change, and re-upserting identical data
      // would churn renders and spam onUpdate callbacks. Compare state too:
      // updatedAt is ms-granular and fast accepted→running transitions can
      // land in the same millisecond (live-feed review finding M3).
      if (
        currentRecord.liveOperation?.updatedAt === operation.updatedAt &&
        currentRecord.liveOperation.state === operation.state
      ) {
        return;
      }

      const feedHandle = mapFeedOperationToHandle(operation);
      lastActivityAtRef.current.set(operationId, Date.now());
      const nextRecord = upsertExecution(operationId, (current) => {
        const base = current ?? currentRecord;
        const mergedHandle = preferLatestHandle(base.handle, feedHandle);
        const feedWins = mergedHandle === feedHandle;

        return {
          ...base,
          handle: mergedHandle,
          liveOperation: operation,
          // Feed frames never carry a terminal reason — preserve one already
          // hydrated from a detail poll (failure copy depends on it exactly
          // on the toasts where it matters most).
          summary: feedWins
            ? {
                ...mapFeedOperationToSummary(operation),
                terminalReason: base.summary?.terminalReason ?? null,
              }
            : base.summary,
        };
      });

      emitExecutionCallbacks(nextRecord, null);

      if (isTerminalExecutionState(nextRecord.handle.state)) {
        cleanupTransport(operationId);
        reconcileTerminalPresentation(operationId);

        // Feed frames carry no terminal reason; when the feed itself delivers
        // the terminal (polling already stopped), fetch detail once so the
        // failure/cancel toast/modal can say WHY.
        if (
          (nextRecord.handle.state === "failed" ||
            nextRecord.handle.state === "cancelled") &&
          !nextRecord.summary?.terminalReason
        ) {
          void hydrateTerminalReason(operationId);
        }
      }
    },
    [
      cleanupTransport,
      emitExecutionCallbacks,
      hydrateTerminalReason,
      reconcileTerminalPresentation,
      upsertExecution,
    ],
  );

  const considerFeedDiscovery = useCallback(
    (operation: ExecutionFeedOperation) => {
      const operationId = operation.operationId;
      if (
        executionsRef.current[operationId] ||
        pendingDiscoveryTimersRef.current.has(operationId) ||
        isExecutionToastSuppressed(operationId) ||
        !isFeedOperationDiscoverable(operation, Date.now())
      ) {
        return;
      }

      // Grace delay so surface-owned recovery (e.g. the trade ticket's
      // auto-reopen of an in-flight order) can claim suppression before the
      // feed auto-surfaces a duplicate toast for the same operation.
      const timeoutId = setTimeout(() => {
        pendingDiscoveryTimersRef.current.delete(operationId);

        // Frames arriving during the grace window don't re-enter this path
        // (the pending timer blocks them) — read the freshest frame at fire
        // time so an op that transitioned mid-window doesn't surface stale
        // (e.g. born "running" when it already completed).
        const latestFrame =
          latestFeedFramesRef.current.get(operationId) ?? operation;

        if (
          executionsRef.current[operationId] ||
          isExecutionToastSuppressed(operationId) ||
          !isFeedOperationDiscoverable(latestFrame, Date.now())
        ) {
          return;
        }

        lastActivityAtRef.current.set(operationId, Date.now());
        const record = upsertExecution(operationId, () => ({
          detail: null,
          displayContext: null,
          handle: mapFeedOperationToHandle(latestFrame),
          liveOperation: latestFrame,
          operationId,
          parkedAtMs: null,
          presentation: "visible" as const,
          source: "user" as const,
          summary: mapFeedOperationToSummary(latestFrame),
          terminalDurationMs: DEFAULT_TERMINAL_DURATION_MS,
          terminalReported: false,
        }));

        emitExecutionCallbacks(record, null);
      }, FEED_DISCOVERY_GRACE_MS);

      pendingDiscoveryTimersRef.current.set(operationId, timeoutId);
    },
    [emitExecutionCallbacks, upsertExecution],
  );

  const publishFeedOperations = useCallback(
    (operations: readonly ExecutionFeedOperation[]) => {
      // The frame map exists solely for pending discovery-grace timers to read
      // the freshest frame at fire time. Rebuild it while any timer pends;
      // otherwise clear it — a timer scheduled by THIS publish (the loop below
      // runs after this check) must fall back to its captured frame, never
      // read a leftover frame from an older publish.
      if (pendingDiscoveryTimersRef.current.size > 0) {
        latestFeedFramesRef.current = new Map(
          operations.map((operation) => [operation.operationId, operation]),
        );
      } else {
        latestFeedFramesRef.current.clear();
      }

      for (const operation of operations) {
        if (executionsRef.current[operation.operationId]) {
          applyFeedOperation(operation);
        } else {
          considerFeedDiscovery(operation);
        }
      }
    },
    [applyFeedOperation, considerFeedDiscovery],
  );

  const trackExecution = useCallback(
    (handle: ExecutionHandle, options?: TrackExecutionOptions): ToastId => {
      const operationId = handle.operationId;

      callbacksRef.current.set(operationId, {
        onTerminal: options?.onTerminal,
        onUpdate: options?.onUpdate,
      });

      lastActivityAtRef.current.set(operationId, Date.now());
      const record = upsertExecution(operationId, (current) => {
        const preferredHandle = preferLatestHandle(
          current?.handle ?? null,
          handle,
        );

        return {
          detail: current?.detail ?? null,
          displayContext: options?.display ?? current?.displayContext ?? null,
          handle: preferredHandle,
          liveOperation: current?.liveOperation ?? null,
          operationId,
          parkedAtMs: current?.parkedAtMs ?? null,
          // An explicit registration is a user action — always (re-)surface.
          presentation: "visible" as const,
          source: options?.source ?? current?.source ?? "user",
          summary: current?.summary ?? null,
          terminalDurationMs:
            options?.terminalDurationMs ??
            current?.terminalDurationMs ??
            DEFAULT_TERMINAL_DURATION_MS,
          terminalReported: current?.terminalReported ?? false,
        };
      });

      emitExecutionCallbacks(record, null);

      if (options?.openOnRegister) {
        setActiveOperationId(operationId);
      }

      if (!isTerminalExecutionState(record.handle.state)) {
        // Polling is the transport until the user feed claims the operation
        // with a frame; it self-stops the moment frames flow.
        startPolling(operationId);
      } else {
        cleanupTransport(operationId);
        reconcileTerminalPresentation(operationId);
      }

      return operationId;
    },
    [
      cleanupTransport,
      emitExecutionCallbacks,
      reconcileTerminalPresentation,
      startPolling,
      upsertExecution,
    ],
  );

  const openExecution = useCallback((operationId: string) => {
    if (!executionsRef.current[operationId]) {
      return;
    }

    setActiveOperationId(operationId);
  }, []);

  useEffect(() => {
    registerToastExecutionBindings({
      dismissExecution: removeExecution,
      openExecution,
      publishFeedOperations,
      trackExecution,
    });

    return () => {
      resetToastExecutionBindings();
    };
  }, [openExecution, publishFeedOperations, removeExecution, trackExecution]);

  useEffect(() => {
    writePersistedTrackedExecutions(storageKey, executions);
  }, [executions, storageKey]);

  useEffect(() => {
    if (previousStorageKeyRef.current !== storageKey) {
      previousStorageKeyRef.current = storageKey;
      clearTrackedExecutions();
    }

    if (!storageKey) {
      return;
    }

    // Once per key per mount: this effect's dep chain includes callback
    // identities, and re-running would re-stamp activity + re-fire callbacks
    // on unrelated re-renders (e.g. the modal opening).
    if (hydratedStorageKeysRef.current.has(storageKey)) {
      return;
    }
    hydratedStorageKeysRef.current.add(storageKey);

    const persisted = readPersistedTrackedExecutions(storageKey);
    if (persisted.length === 0) {
      return;
    }

    for (const entry of persisted) {
      lastActivityAtRef.current.set(entry.operationId, Date.now());
      const record = upsertExecution(entry.operationId, (current) => ({
        detail: current?.detail ?? null,
        displayContext:
          entry.displayContext ?? current?.displayContext ?? null,
        handle: preferLatestHandle(current?.handle ?? null, entry.handle),
        liveOperation: current?.liveOperation ?? null,
        operationId: entry.operationId,
        parkedAtMs: current?.parkedAtMs ?? null,
        presentation: entry.presentation ?? current?.presentation ?? "visible",
        source: entry.source ?? current?.source ?? "user",
        summary: current?.summary ?? null,
        terminalDurationMs:
          entry.terminalDurationMs ??
          current?.terminalDurationMs ??
          DEFAULT_TERMINAL_DURATION_MS,
        terminalReported:
          entry.terminalReported ?? current?.terminalReported ?? false,
      }));

      emitExecutionCallbacks(record, null);

      if (!isTerminalExecutionState(record.handle.state)) {
        startPolling(record.operationId);
      }
    }
  }, [
    clearTrackedExecutions,
    emitExecutionCallbacks,
    startPolling,
    storageKey,
    upsertExecution,
  ]);

  // While the detail modal is open: hydrate immediately, then poll every 2s
  // until the operation is terminal. Detail is fully populated for worker ops
  // too (both executors write the same Postgres narrative), so this is the
  // modal's timeline source regardless of which live channel is healthy.
  useEffect(() => {
    if (!activeOperationId) {
      return;
    }

    let cancelled = false;

    const fetchDetail = async () => {
      try {
        const detail = await platformApi.v2.executions.detail.query({
          operationId: activeOperationId,
        });

        const seedRecord = executionsRef.current[activeOperationId];
        if (cancelled || !seedRecord) {
          return;
        }

        const hydratedState = applyExecutionDetail(detail);
        const nextRecord = upsertExecution(activeOperationId, (current) => {
          const base = current ?? seedRecord;
          return {
            ...base,
            detail: hydratedState.detail,
            handle: preferLatestHandle(base.handle, hydratedState.handle),
            summary: hydratedState.summary,
          };
        });

        emitExecutionCallbacks(nextRecord, null);

        if (isTerminalExecutionState(nextRecord.handle.state)) {
          cleanupTransport(activeOperationId);
          reconcileTerminalPresentation(activeOperationId);
        }
      } catch {
        // Keep the modal open with the best-known state.
      }
    };

    void fetchDetail();

    let lastPolledAtMs = Date.now();
    const intervalId = setInterval(() => {
      const record = executionsRef.current[activeOperationId];
      if (!record || isTerminalExecutionState(record.handle.state)) {
        return;
      }
      // While the run-DO socket narrates the timeline live, detail is only a
      // slow refresher; poll fast only when the socket is not delivering.
      const intervalMs =
        operationLiveStatusRef.current === "live"
          ? WATCH_POLL_INTERVAL_MS
          : POLL_INTERVAL_MS;
      if (Date.now() - lastPolledAtMs < intervalMs) {
        return;
      }
      lastPolledAtMs = Date.now();
      void fetchDetail();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [
    activeOperationId,
    cleanupTransport,
    emitExecutionCallbacks,
    platformApi,
    reconcileTerminalPresentation,
    upsertExecution,
  ]);

  // Watchdog sweep: settlement-park bookkeeping + park auto-dismiss into
  // watching + idle-feed detection ("no live frame for a while" is a
  // first-class condition that must arm polling, not an edge case). The
  // interval is mounted ONCE and reads everything through refs — keying it on
  // state identity would reset its phase on every commit, and a 2s poll
  // cadence would then starve a 2.5s interval forever.
  const sweepRef = useRef<() => void>(() => undefined);
  sweepRef.current = () => {
    const now = Date.now();
    for (const record of Object.values(executionsRef.current)) {
      if (isTerminalExecutionState(record.handle.state)) {
        continue;
      }

      const parked = isRecordParked(record);
      if (parked && record.parkedAtMs === null) {
        upsertExecution(record.operationId, (current) => ({
          ...(current ?? record),
          parkedAtMs: now,
        }));
      } else if (!parked && record.parkedAtMs !== null) {
        upsertExecution(record.operationId, (current) => ({
          ...(current ?? record),
          parkedAtMs: null,
        }));
      }

      if (
        parked &&
        record.presentation === "visible" &&
        record.parkedAtMs !== null &&
        now - record.parkedAtMs >= PARK_AUTO_DISMISS_MS
      ) {
        transitionExecutionToWatching(record.operationId);
      }

      const lastActivityAtMs =
        lastActivityAtRef.current.get(record.operationId) ?? 0;
      if (
        now - lastActivityAtMs >= IDLE_WATCHDOG_MS &&
        !pollingTimeoutsRef.current.has(record.operationId) &&
        !pollingActiveRef.current.has(record.operationId)
      ) {
        startPolling(record.operationId);
      }
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      sweepRef.current();
    }, WATCHDOG_SWEEP_INTERVAL_MS);
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  /** User dismissal from the pill: terminal → remove; active → watching. */
  const dismissExecutionFromUi = useCallback(
    (operationId: string) => {
      const current = executionsRef.current[operationId];
      if (!current) {
        return;
      }

      if (isTerminalExecutionState(current.handle.state)) {
        removeExecution(operationId);
      } else {
        transitionExecutionToWatching(operationId);
      }
    },
    [removeExecution, transitionExecutionToWatching],
  );

  const simpleToasts = useSyncExternalStore(
    subscribeSimpleToasts,
    getSimpleToasts,
    getSimpleToasts,
  );

  // Auto-close (v1 semantics): terminal execution pills dismiss after their
  // terminal duration, simple pills after theirs. Hovering the stack or
  // having the modal open freezes everything (full re-arm on release, like
  // v1). Timers are keyed per toast and armed ONCE on eligibility — unrelated
  // state churn (another op's poll/feed frames) must never reset a running
  // countdown, or busy sessions would pin toasts on screen forever.
  useEffect(() => {
    const timers = autoCloseTimersRef.current;

    const clearAll = () => {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
      timers.clear();
    };

    if (isStackHovered || activeOperationId !== null) {
      clearAll();
      return;
    }

    const eligible = new Map<string, { close: () => void; durationMs: number }>();
    for (const record of Object.values(executions)) {
      if (
        isTerminalExecutionState(record.handle.state) &&
        record.presentation === "visible" &&
        !isExecutionToastSuppressed(record.operationId)
      ) {
        eligible.set(`execution:${record.operationId}`, {
          close: () => removeExecution(record.operationId),
          durationMs: record.terminalDurationMs,
        });
      }
    }
    for (const simpleToast of simpleToasts) {
      if (Number.isFinite(simpleToast.durationMs)) {
        eligible.set(`simple:${String(simpleToast.id)}`, {
          close: () => dismissSimpleToast(simpleToast.id),
          durationMs: simpleToast.durationMs,
        });
      }
    }

    for (const [key, timer] of timers) {
      if (!eligible.has(key)) {
        clearTimeout(timer);
        timers.delete(key);
      }
    }
    for (const [key, entry] of eligible) {
      if (!timers.has(key)) {
        timers.set(
          key,
          setTimeout(() => {
            timers.delete(key);
            entry.close();
          }, entry.durationMs),
        );
      }
    }
    // No cleanup on dep changes — stale timers are pruned by the diff above,
    // and the freeze/unmount paths clear the map explicitly.
  }, [
    activeOperationId,
    executions,
    isStackHovered,
    removeExecution,
    simpleToasts,
    suppressionVersion,
  ]);

  useEffect(() => {
    disposedRef.current = false;

    return () => {
      disposedRef.current = true;

      for (const timeoutId of pendingDiscoveryTimersRef.current.values()) {
        clearTimeout(timeoutId);
      }
      pendingDiscoveryTimersRef.current.clear();

      for (const timer of autoCloseTimersRef.current.values()) {
        clearTimeout(timer);
      }
      autoCloseTimersRef.current.clear();

      for (const operationId of Object.keys(executionsRef.current)) {
        cleanupTransport(operationId);
      }
    };
  }, [cleanupTransport]);

  // The expanded modal rides the per-operation run-DO WebSocket; the status
  // ref lets the modal's detail poll drop to the slow cadence while the
  // socket is live.
  const operationLive = useExecutionOperationLive(activeOperationId);
  operationLiveStatusRef.current = operationLive.status;

  const activeExecution = activeOperationId
    ? (executions[activeOperationId] ?? null)
    : null;

  return {
    activeExecution,
    activeOperationId,
    dismissExecutionFromUi,
    executions,
    isStackHovered,
    operationLive,
    setActiveOperationId,
    setIsStackHovered,
    simpleToasts,
    suppressionVersion,
  };
}

export function ToastProvider() {
  const {
    activeExecution,
    activeOperationId,
    dismissExecutionFromUi,
    executions,
    isStackHovered,
    operationLive,
    setActiveOperationId,
    setIsStackHovered,
    simpleToasts,
    suppressionVersion,
  } = useTrackedExecutionToasts();

  const items = useMemo((): CenterToastItem[] => {
    // suppressionVersion invalidates this memo when the registry changes.
    void suppressionVersion;
    const executionItems = Object.values(executions)
      .filter(
        (record) =>
          record.presentation === "visible" &&
          !isExecutionToastSuppressed(record.operationId),
      )
      .map(
        (record): CenterToastItem => ({
          createdAtMs: Date.parse(record.handle.createdAt) || 0,
          key: `execution:${record.operationId}`,
          kind: "execution",
          view: buildExecutionToastView(record),
        }),
      );
    const simpleItems = simpleToasts.map(
      (record): CenterToastItem => ({
        createdAtMs: record.createdAtMs,
        key: `simple:${String(record.id)}`,
        kind: "simple",
        record,
      }),
    );

    return [...executionItems, ...simpleItems].sort(
      (left, right) => right.createdAtMs - left.createdAtMs,
    );
  }, [executions, simpleToasts, suppressionVersion]);

  const activeView = activeExecution
    ? buildExecutionToastView(activeExecution)
    : null;

  return (
    <>
      {/* The stack stays MOUNTED at all times; while its own modal is open
          it receives an empty item list instead of unmounting (v1's
          crossfade: pills animate out under the opening modal and rise back
          when it closes — an unmount would delete them with no exit). */}
      <CenterToastStack
        isHovered={isStackHovered}
        items={activeOperationId === null ? items : EMPTY_TOAST_ITEMS}
        onDismissExecution={dismissExecutionFromUi}
        onDismissSimple={(key) => {
          const item = items.find(
            (candidate) => candidate.key === key && candidate.kind === "simple",
          );
          if (item?.kind === "simple") {
            dismissSimpleToast(item.record.id);
          }
        }}
        onExpandExecution={setActiveOperationId}
        onHoverChange={setIsStackHovered}
      />
      <ExecutionToastModal
        detail={activeExecution?.detail ?? null}
        liveEvents={operationLive.events}
        onDismissOperation={() => {
          if (activeOperationId) {
            dismissExecutionFromUi(activeOperationId);
          }
        }}
        onOpenChange={(open) => {
          if (!open) {
            setActiveOperationId(null);
          }
        }}
        open={activeOperationId !== null}
        view={activeView}
      />
    </>
  );
}
